import React from "react";
import {
  BarChart3,
  Building,
  CheckCircle,
  Clock,
  CreditCard,
  FileText,
  Home,
  Layers,
  MapPin,
  PoundSterling,
  RefreshCw,
  Search,
  Settings,
  Smartphone,
  Users,
  Zap,
  X
} from "lucide-react";
import {
  ActionGroup,
  Button,
  Cell,
  Column,
  Flex,
  Heading,
  Item,
  Menu,
  MenuTrigger,
  Provider,
  Row,
  TableBody,
  TableHeader,
  TableView,
  TabList,
  TabPanels,
  Tabs,
  Text,
  View
} from "@adobe/react-spectrum";
import { customSpectrumTheme, SPECTRUM_THEME_CLASS } from "../../themes/spectrum-theme";
import {Link} from "react-router-dom";
import { formatCurrency } from "../../common/utils";
import Content from "../ui/content";
import dashboardStyles, { hoverEffects } from "./styles";

class Dashboard extends React.Component<any, any> {
  constructor(props) {
    super(props);
    this.state = {
      selectedTab: 'organisations'
    };
  }

  formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const activeLanguage = this.props.activeLanguage === "jp-JP" ? "ja-JP" : this.props.activeLanguage;
    return date.toLocaleDateString(activeLanguage || "en-GB");
  };

  getStatusBadgeColor = (status) => {
    const statusMap = {
      Active: "#4caf50",
      PastDue: "#ff9800",
      Pending: "#2196f3",
      Canceled: "#f44336",
      Failed: "#f44336",
    };
    return statusMap[status] || "#999";
  };

  renderMetricsCards = () => {
    const {dashboard, translate} = this.props;
    const {overview} = dashboard;

    const kpis = [
      {
        label: translate("total_organisations"),
        value: String(overview.totalOrganisations || "0"),
        icon: Building
      },
      {
        label: translate("total_devices"),
        value: String(overview.totalDevices || "0"),
        icon: Smartphone
      },
      {
        label: translate("active_subscriptions"),
        value: String(overview.activeSubscriptions || "0"),
        icon: CheckCircle
      },
      {
        label: translate("monthly_revenue"),
        value: formatCurrency(overview.totalMonthlyRevenueGBP || 0, "GBP"),
        icon: PoundSterling
      }
    ];

    return (
      <div style={dashboardStyles.kpiGrid}>
        {kpis.map((kpi) => {
          const IconComponent = kpi.icon;
          return (
            <div key={kpi.label} style={dashboardStyles.kpiCard}
                 onMouseEnter={(e) => Object.assign(e.currentTarget.style, hoverEffects.kpiCardHover)}
                 onMouseLeave={(e) => Object.assign(e.currentTarget.style, hoverEffects.kpiCardDefault)}>
              <div style={dashboardStyles.kpiIcon}>
                <IconComponent size={28} color="var(--spectrum-accent-background-color-default, #005795)"/>
              </div>
              <div style={dashboardStyles.kpiContent}>
                <Text UNSAFE_style={dashboardStyles.kpiLabel}>
                  {kpi.label}
                </Text>
                <Heading level={2} marginBottom={0} marginTop={0}
                         UNSAFE_style={dashboardStyles.kpiValue}>
                  {kpi.value}
                </Heading>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  renderQuickActions = () => {
    const {translate} = this.props;

    const quickActions = [
      {
        id: 'organisations',
        label: translate('view_organisations'),
        description: translate('manage_all_organisations'),
        icon: Building,
        link: '#organisations'
      },
      {
        id: 'subscriptions',
        label: translate('view_subscriptions'),
        description: translate('manage_subscriptions'),
        icon: CheckCircle,
        link: '#subscriptions'
      },
      {
        id: 'all-jobs',
        label: translate('all_jobs'),
        description: translate('view_manage_all_jobs'),
        icon: Layers,
        link: '/jobs'
      },
      {
        id: 'all-devices',
        label: translate('all_devices'),
        description: translate('view_manage_all_devices'),
        icon: Smartphone,
        link: '/devices'
      },
      {
        id: 'all-users',
        label: translate('all_users'),
        description: translate('manage_user_accounts'),
        icon: Users,
        link: '/allusers'
      },
      {
        id: 'metrics',
        label: translate('organisation_metrics_label'),
        description: translate('view_metrics_analytics'),
        icon: BarChart3,
        link: '/organisation-metrics'
      },
      {
        id: 'smart-flags',
        label: translate('smart_flags_label'),
        description: translate('manage_feature_flags'),
        icon: Zap,
        link: '/smart-flags'
      },
      {
        id: 'job-logs',
        label: translate('job_logs'),
        description: translate('background_job_history'),
        icon: Clock,
        link: '/job-logs'
      },
      {
        id: 'settings',
        label: translate('system_settings'),
        description: translate('configure_system_settings'),
        icon: Settings,
        link: '/settings'
      },
      {
        id: 'subscription-management',
        label: translate('subscription_management'),
        description: translate('manage_tiers_pricing'),
        icon: CreditCard,
        link: '/subscription-management'
      },
      {
        id: 'reports',
        label: translate('reports'),
        description: translate('view_download_reports'),
        icon: FileText,
        link: '/reportlist'
      },
      {
        id: 'location-tracking',
        label: translate('location_tracking'),
        description: translate('track_device_locations'),
        icon: MapPin,
        link: '/locationTracking'
      },
      {
        id: 'home',
        label: translate('home'),
        description: translate('user_home_page'),
        icon: Home,
        link: '/home'
      }
    ];

    return (
      <View marginTop="size-10" marginBottom="size-10">
        <Heading marginTop={0} level={2} marginBottom="size-200">{translate('quick_actions')}</Heading>
        <div style={dashboardStyles.quickActionsScroll}>
          {quickActions.map((action) => {
            const ActionIcon = action.icon;
            const isHashLink = action.link.startsWith('#');

            const content = (
              <div style={dashboardStyles.quickActionCard}
                   onMouseEnter={(e) => Object.assign(e.currentTarget.style, hoverEffects.quickActionHover)}
                   onMouseLeave={(e) => Object.assign(e.currentTarget.style, hoverEffects.quickActionDefault)}>
                <ActionIcon size={32} color="var(--spectrum-accent-background-color-default, #005795)" strokeWidth={1.5}/>
                <Heading level={4} marginTop={0} marginBottom={0}
                         UNSAFE_style={dashboardStyles.quickActionLabel}>{action.label}</Heading>
                <Text UNSAFE_style={dashboardStyles.quickActionDesc}>{action.description}</Text>
              </div>
            );

            if (isHashLink) {
              return (
                <div key={action.id}
                     onClick={() => this.setState({selectedTab: action.link.substring(1)})}>
                  {content}
                </div>
              );
            }

            return (
              <Link key={action.id} to={action.link}
                    style={{textDecoration: 'none'}}>
                {content}
              </Link>
            );
          })}
        </div>
      </View>
    );
  };

  renderOrganisationsTable = () => {
    const {
      dashboard,
      onOrganisationRowClick,
      onOrganisationPageChange,
      organisationsPage,
      translate,
    } = this.props;
    const {organisations} = dashboard;

    return (
      <View>
        <TableView aria-label={translate("total_organisations")}>
          <TableHeader>
            <Column>{translate("name")}</Column>
            <Column>{translate("contact_email")}</Column>
            <Column>{translate("city")}</Column>
            <Column>{translate("country")}</Column>
            <Column>{translate("total_devices")}</Column>
            <Column>{translate("active_subscription")}</Column>
            <Column>{translate("createdat")}</Column>
            <Column>{translate("action")}</Column>
          </TableHeader>
          <TableBody>
            {organisations.data &&
              organisations.data.map((org) => (
                <Row key={org.id}>
                  <Cell>{org.name}</Cell>
                  <Cell>{org.contactEmail || "-"}</Cell>
                  <Cell>{org.city || "-"}</Cell>
                  <Cell>{org.country || "-"}</Cell>
                  <Cell>{org.deviceCount}</Cell>
                  <Cell>
                    {org.activeSubscriptionCount > 0 ? (
                      <span style={{fontSize: "14px", color: "var(--spectrum-positive-visual-color, #4caf50)"}}>
                        ✓
                      </span>
                    ) : (
                      <span style={{fontSize: "14px", color: "#ccc"}}>✗</span>
                    )}
                  </Cell>
                  <Cell>{this.formatDate(org.createdDate)}</Cell>
                  <Cell>
                    <ActionGroup
                      density="compact"
                      orientation="horizontal"
                      onAction={(key) => {
                        if (key === 'view') onOrganisationRowClick(org.id)
                      }}
                    >
                      <Item key="view">{translate("general_view")}</Item>
                    </ActionGroup>
                  </Cell>
                </Row>
              ))}
          </TableBody>
        </TableView>

        {organisations.pagination.totalPages > 1 && (
          <Flex
            gap="size-200"
            alignItems="center"
            justifyContent="center"
            marginTop="size-200"
          >
            <Button
              variant="secondary"
              isDisabled={organisationsPage === 1}
              onPress={() => onOrganisationPageChange(organisationsPage - 1)}
            >
              {translate("general_table_previous")}
            </Button>
            <Text>
              {translate("general_table_page")} {organisationsPage}{" "}
              {translate("general_table_of")}{" "}
              {organisations.pagination.totalPages}
            </Text>
            <Button
              variant="secondary"
              isDisabled={
                organisationsPage === organisations.pagination.totalPages
              }
              onPress={() => onOrganisationPageChange(organisationsPage + 1)}
            >
              {translate("general_table_next")}
            </Button>
          </Flex>
        )}
      </View>
    );
  };

  renderSubscriptionsTable = () => {
    const {
      dashboard,
      onSubscriptionRowClick,
      onSubscriptionsPageChange,
      subscriptionsPage,
      translate,
    } = this.props;
    const {subscriptions} = dashboard;

    return (
      <View>
        <TableView aria-label={translate("total_subscriptions")}>
          <TableHeader>
            <Column>{translate("result_organisation")}</Column>
            <Column>{translate("status")}</Column>
            <Column>{translate("pricing_tier")}</Column>
            <Column>{translate("total_devices")}</Column>
            <Column>{translate("monthly_price")}</Column>
            <Column>{translate("general_start_date")}</Column>
            <Column>{translate("period_end")}</Column>
            <Column>{translate("action")}</Column>
          </TableHeader>
          <TableBody>
            {subscriptions.data &&
              subscriptions.data.map((sub) => (
                <Row key={sub.id}>
                  <Cell>{sub.organisationName}</Cell>
                  <Cell>
                    <span
                      style={{
                        backgroundColor:
                          sub.status === "Active" ? "#e8f5e9" : "#fff3e0",
                        color: sub.status === "Active" ? "var(--spectrum-positive-visual-color, #4caf50)" : this.getStatusBadgeColor(sub.status),
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                      }}
                    >
                      {sub.status}
                    </span>
                  </Cell>
                  <Cell>{sub.subscriptionTier}</Cell>
                  <Cell>{sub.deviceCount}</Cell>
                  <Cell>{formatCurrency(sub.monthlyPrice || sub.monthlyPriceGBP || 0, sub.currency || "GBP")}</Cell>
                  <Cell>{this.formatDate(sub.startDate)}</Cell>
                  <Cell>{this.formatDate(sub.currentPeriodEnd)}</Cell>
                  <Cell>
                    <ActionGroup
                      density="compact"
                      orientation="horizontal"
                      onAction={(key) => {
                        if (key === 'view') onSubscriptionRowClick(sub.id)
                      }}
                    >
                      <Item key="view">{translate("general_view")}</Item>
                    </ActionGroup>
                  </Cell>
                </Row>
              ))}
          </TableBody>
        </TableView>

        {subscriptions.pagination.totalPages > 1 && (
          <Flex
            gap="size-200"
            alignItems="center"
            justifyContent="center"
            marginTop="size-200"
          >
            <Button
              variant="secondary"
              isDisabled={subscriptionsPage === 1}
              onPress={() => onSubscriptionsPageChange(subscriptionsPage - 1)}
            >
              {translate("general_table_previous")}
            </Button>
            <Text>
              {translate("general_table_page")} {subscriptionsPage}{" "}
              {translate("general_table_of")}{" "}
              {subscriptions.pagination.totalPages}
            </Text>
            <Button
              variant="secondary"
              isDisabled={
                subscriptionsPage === subscriptions.pagination.totalPages
              }
              onPress={() => onSubscriptionsPageChange(subscriptionsPage + 1)}
            >
              {translate("general_table_next")}
            </Button>
          </Flex>
        )}
      </View>
    );
  };

  renderTabSearch = () => {
    const {
      organisationsSearch,
      subscriptionsSearch,
      onOrganisationSearchChange,
      onSubscriptionsSearchChange,
      translate,
    } = this.props;
    const {selectedTab} = this.state;
    const isSubscriptions = selectedTab === "subscriptions";
    const value = isSubscriptions ? subscriptionsSearch || "" : organisationsSearch || "";
    const onChange = (nextValue) => {
      if (isSubscriptions) {
        if (onSubscriptionsSearchChange) onSubscriptionsSearchChange(nextValue);
        return;
      }

      if (onOrganisationSearchChange) onOrganisationSearchChange(nextValue);
    };

    return (
      <View
        UNSAFE_style={{
          position: "absolute",
          top: "6px",
          right: "12px",
          zIndex: 2,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "28px",
            border: "1px solid #c9c9c9",
            borderRadius: "4px",
            background: "#fff",
            padding: "0 4px 0 8px",
            boxSizing: "border-box",
            width: "220px"
          }}
        >
          <Search size={14} color="#666" style={{flexShrink: 0, marginRight: "6px"}} />
          <input
            aria-label={translate("search")}
            placeholder={translate("search")}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            style={{
              border: "none",
              outline: "none",
              flex: 1,
              minWidth: 0,
              height: "24px",
              fontSize: "12px",
              background: "transparent"
            }}
          />
          {value ? (
            <button
              type="button"
              aria-label={translate("general_close")}
              onClick={() => onChange("")}
              style={{
                border: "none",
                background: "transparent",
                padding: "2px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#666"
              }}
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
      </View>
    );
  };

  render() {
    const {
      translate,
      onTermsAcceptanceLogsClick,
      onRefreshDashboard
    } = this.props;
    const {selectedTab} = this.state;

    return (
      <Provider theme={customSpectrumTheme} UNSAFE_className={SPECTRUM_THEME_CLASS} colorScheme="light">
        <div>
          <Content translate={translate}>
            <View UNSAFE_style={{
              maxWidth: '1200px',
              margin: '0 auto',
              paddingTop: '60px'
            }}>
              <Flex direction="column" gap="size-200">
                {/* Header */}
                <View>
                  <Flex justifyContent="space-between" alignItems="center"
                        gap="size-100">
                    <Heading level={1} UNSAFE_style={{margin: 0}}>{translate('admin_dashboard')}</Heading>
                    <Flex gap="size-100" alignItems="center">
                      <MenuTrigger>
                        <Button variant="secondary">
                          <Settings size={18} style={{marginRight: '8px'}}/>
                          {translate('settings')}
                        </Button>
                        <Menu
                          onAction={(key) => {
                            if (key === "terms-logs") {
                              onTermsAcceptanceLogsClick();
                            }
                          }}
                        >
                          <Item key="terms-logs">
                            {translate("terms_acceptance_logs")}
                          </Item>
                        </Menu>
                      </MenuTrigger>
                      <Button
                        variant="secondary"
                        onPress={() => onRefreshDashboard && onRefreshDashboard()}
                      >
                        <RefreshCw size={18} style={{marginRight: '8px'}}/>
                        {translate('refresh')}
                      </Button>
                    </Flex>
                  </Flex>
                </View>

                {/* KPI Cards */}
                {this.renderMetricsCards()}

                {/* Quick Actions Section */}
                {this.renderQuickActions()}

                {/* Tables in Tabs */}
                <View UNSAFE_style={{background: '#fff', borderRadius: '4px', position: 'relative'}}
                      marginTop="12px">
                  {this.renderTabSearch()}
                  <Tabs
                    selectedKey={selectedTab}
                    onSelectionChange={(key) => this.setState({selectedTab: key})}
                  >
                    <TabList>
                      <Item
                        key="organisations">{translate("organisations")}</Item>
                      <Item
                        key="subscriptions">{translate("subscriptions")}</Item>
                    </TabList>
                    <TabPanels>
                      <Item key="organisations">
                        <View padding="size-100">
                          {this.renderOrganisationsTable()}
                        </View>
                      </Item>
                      <Item key="subscriptions">
                        <View padding="size-100">
                          {this.renderSubscriptionsTable()}
                        </View>
                      </Item>
                    </TabPanels>
                  </Tabs>
                </View>
              </Flex>
            </View>
          </Content>

        </div>
      </Provider>
    );
  }
}

export default Dashboard;
